/**
 * AdminAnalyticsTab Component
 * 
 * Displays analytics dashboard including:
 * - Key metrics (users, signups, sessions, revenue)
 * - Quick Insights (AI-powered pattern detection)
 * - Page views analysis
 * - User engagement metrics
 * - Top members leaderboard
 * - Follow-up Email Analytics
 * - A/B Testing dashboard
 */

import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { parseLocalDate } from '@/utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, 
  RefreshCw, 
  Download, 
  Mail, 
  FlaskConical, 
  Pause, 
  Play, 
  RotateCcw, 
  TrendingUp,
  Diamond,
  Eye,
  Clock,
  LogIn,
  Users,
  Activity,
  Lightbulb,
  Zap,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const API_URL = window.location.origin;

// Helper function to generate insights from analytics data
const generateInsights = (analyticsData, pageViewsData) => {
  const insights = [];
  
  if (!analyticsData && !pageViewsData) return insights;
  
  // Analyze signup patterns
  const dailySignups = analyticsData?.user_acquisition?.daily_signups || [];
  if (dailySignups.length > 0) {
    // Find peak signup day
    const maxSignup = dailySignups.reduce((max, d) => d.count > max.count ? d : max, { count: 0 });
    if (maxSignup.count > 0) {
      const peakDate = parseLocalDate(maxSignup.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      insights.push({
        type: 'peak',
        icon: Zap,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        title: 'Peak Signup Day',
        message: `${peakDate} had the highest signups with ${maxSignup.count} new users`,
        priority: maxSignup.count > 10 ? 'high' : 'medium'
      });
    }
    
    // Calculate average and find anomalies
    const totalSignups = dailySignups.reduce((sum, d) => sum + d.count, 0);
    const avgSignups = totalSignups / dailySignups.length;
    const recentDays = dailySignups.slice(-7);
    const recentAvg = recentDays.reduce((sum, d) => sum + d.count, 0) / recentDays.length;
    
    if (recentAvg > avgSignups * 1.5 && avgSignups > 0) {
      insights.push({
        type: 'trend',
        icon: TrendingUp,
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50',
        title: 'Signup Momentum',
        message: `Recent signups are ${Math.round((recentAvg / avgSignups - 1) * 100)}% above average - great momentum!`,
        priority: 'high'
      });
    } else if (recentAvg < avgSignups * 0.5 && avgSignups > 1) {
      insights.push({
        type: 'trend',
        icon: ArrowDownRight,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        title: 'Signup Slowdown',
        message: `Recent signups are ${Math.round((1 - recentAvg / avgSignups) * 100)}% below average - consider a campaign boost`,
        priority: 'medium'
      });
    }
  }
  
  // Analyze traffic patterns
  const dailyTraffic = pageViewsData?.daily_traffic || [];
  if (dailyTraffic.length > 0) {
    // Find best traffic day
    const maxTraffic = dailyTraffic.reduce((max, d) => d.views > max.views ? d : max, { views: 0 });
    if (maxTraffic.views > 0) {
      const trafficDate = parseLocalDate(maxTraffic.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      insights.push({
        type: 'traffic',
        icon: Eye,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        title: 'Highest Traffic Day',
        message: `${trafficDate} saw ${maxTraffic.views} page views from ${maxTraffic.visitors} visitors`,
        priority: 'medium'
      });
    }
    
    // Analyze weekend vs weekday patterns
    const weekdayTraffic = [];
    const weekendTraffic = [];
    dailyTraffic.forEach(d => {
      const day = parseLocalDate(d.date).getDay();
      if (day === 0 || day === 6) {
        weekendTraffic.push(d.views);
      } else {
        weekdayTraffic.push(d.views);
      }
    });
    
    if (weekdayTraffic.length > 0 && weekendTraffic.length > 0) {
      const avgWeekday = weekdayTraffic.reduce((a, b) => a + b, 0) / weekdayTraffic.length;
      const avgWeekend = weekendTraffic.reduce((a, b) => a + b, 0) / weekendTraffic.length;
      
      if (avgWeekday > avgWeekend * 1.5) {
        insights.push({
          type: 'pattern',
          icon: Calendar,
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          title: 'Weekday Focus',
          message: `Traffic is ${Math.round((avgWeekday / avgWeekend - 1) * 100)}% higher on weekdays - optimize for business hours`,
          priority: 'low'
        });
      } else if (avgWeekend > avgWeekday * 1.5) {
        insights.push({
          type: 'pattern',
          icon: Calendar,
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          title: 'Weekend Warriors',
          message: `Traffic is ${Math.round((avgWeekend / avgWeekday - 1) * 100)}% higher on weekends - your audience engages on personal time`,
          priority: 'low'
        });
      }
    }
  }
  
  // Engagement insights
  const engagement = analyticsData?.engagement;
  if (engagement) {
    const activeRate = engagement.active_rate || 0;
    if (activeRate > 10) {
      insights.push({
        type: 'engagement',
        icon: Sparkles,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        title: 'Strong Engagement',
        message: `${activeRate.toFixed(1)}% of users are actively engaging - above industry average!`,
        priority: 'high'
      });
    } else if (activeRate < 3 && analyticsData?.user_acquisition?.total_users > 50) {
      insights.push({
        type: 'engagement',
        icon: Users,
        color: 'text-rose-600',
        bgColor: 'bg-rose-50',
        title: 'Re-engagement Opportunity',
        message: `Only ${activeRate.toFixed(1)}% are active - consider an email campaign to re-engage dormant users`,
        priority: 'high'
      });
    }
  }
  
  // Growth insight
  const changePercent = analyticsData?.user_acquisition?.change_percent;
  if (changePercent !== undefined) {
    if (changePercent > 50) {
      insights.push({
        type: 'growth',
        icon: ArrowUpRight,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        title: 'Exceptional Growth',
        message: `+${changePercent.toFixed(0)}% signup growth vs previous period - you're on fire!`,
        priority: 'high'
      });
    }
  }
  
  // Sort by priority
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  insights.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  
  return insights.slice(0, 4); // Return top 4 insights
};

const AdminAnalyticsTab = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPreset, setAnalyticsPreset] = useState('30d');
  const [analyticsCustomStart, setAnalyticsCustomStart] = useState('');
  const [analyticsCustomEnd, setAnalyticsCustomEnd] = useState('');
  const [exportingCSV, setExportingCSV] = useState(false);
  const [pageViewsData, setPageViewsData] = useState(null);
  const [emailAnalytics, setEmailAnalytics] = useState(null);
  const [abTests, setAbTests] = useState([]);
  const [cohortData, setCohortData] = useState(null);
  const [reengagementData, setReengagementData] = useState(null);
  const [sendingReengagement, setSendingReengagement] = useState(false);

  // Generate insights from current data
  const insights = useMemo(() => {
    return generateInsights(analyticsData, pageViewsData);
  }, [analyticsData, pageViewsData]);

  const dailyTrafficChartData = useMemo(() => (
    (pageViewsData?.daily_traffic || []).map(d => ({
      date: parseLocalDate(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      views: d.views,
      visitors: d.visitors
    })).sort((a, b) => new Date(a.date) - new Date(b.date))
  ), [pageViewsData]);

  const activeCohorts = useMemo(() => (
    (cohortData?.cohorts || []).filter(c => c.total_signups > 0)
  ), [cohortData]);

  useEffect(() => {
    fetchAnalytics();
  }, [analyticsPreset, analyticsCustomStart, analyticsCustomEnd]);

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      let url = `${API_URL}/api/admin/analytics/dashboard?preset=${analyticsPreset}`;
      let pageViewsUrl = `${API_URL}/api/admin/analytics/page-views`;
      const days = analyticsPreset === '7d' ? 7 : analyticsPreset === '90d' ? 90 : 30;
      let emailAnalyticsUrl = `${API_URL}/api/admin/email-analytics/followup?days=${days}`;
      
      if (analyticsPreset === 'custom' && analyticsCustomStart && analyticsCustomEnd) {
        url += `&start_date=${analyticsCustomStart}&end_date=${analyticsCustomEnd}`;
        pageViewsUrl += `?start_date=${analyticsCustomStart}&end_date=${analyticsCustomEnd}`;
      } else {
        const now = new Date();
        const start = new Date(now - days * 24 * 60 * 60 * 1000);
        pageViewsUrl += `?start_date=${start.toISOString()}&end_date=${now.toISOString()}`;
      }
      
      const [response, pageViewsResponse, emailResponse, abTestsResponse, cohortResponse, reengagementResponse] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(pageViewsUrl, { headers }),
        axios.get(emailAnalyticsUrl, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/ab-tests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/analytics/cohort-retention?months=6`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/reengagement/stats?days=30`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setAnalyticsData(response.data);
      setPageViewsData(pageViewsResponse.data);
      setEmailAnalytics(emailResponse.data);
      setAbTests(abTestsResponse.data || []);
      setCohortData(cohortResponse.data);
      setReengagementData(reengagementResponse.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const toggleAbTest = async (testId, isActive) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/ab-tests/${testId}`, { is_active: !isActive }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`A/B test ${!isActive ? 'activated' : 'paused'}`);
      fetchAnalytics();
    } catch (error) {
      console.error('Error toggling A/B test:', error);
      toast.error('Failed to update A/B test');
    }
  };

  const resetAbTest = async (testId) => {
    if (!window.confirm('Are you sure you want to reset this A/B test? All results will be cleared.')) {
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/ab-tests/${testId}/reset`);
      toast.success('A/B test results reset');
      fetchAnalytics();
    } catch (error) {
      console.error('Error resetting A/B test:', error);
      toast.error('Failed to reset A/B test');
    }
  };

  const exportAnalyticsCSV = async (reportType) => {
    setExportingCSV(true);
    try {
      let url = `${API_URL}/api/admin/analytics/export?report_type=${reportType}`;
      if (analyticsPreset === 'custom' && analyticsCustomStart && analyticsCustomEnd) {
        url += `&start_date=${analyticsCustomStart}&end_date=${analyticsCustomEnd}`;
      } else {
        const now = new Date();
        const days = analyticsPreset === '7d' ? 7 : analyticsPreset === '90d' ? 90 : 30;
        const start = new Date(now - days * 24 * 60 * 60 * 1000);
        url += `&start_date=${start.toISOString()}&end_date=${now.toISOString()}`;
      }
      
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `analytics_${reportType}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExportingCSV(false);
    }
  };

  const sendReengagementEmails = async (targetGroup = 'all') => {
    if (!window.confirm(`Send re-engagement emails to ${targetGroup === 'all' ? 'all dormant' : targetGroup} users?`)) {
      return;
    }
    
    setSendingReengagement(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/reengagement/send-emails?target_group=${targetGroup}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { sent, skipped, failed } = response.data;
      toast.success(`Re-engagement emails: ${sent} sent, ${skipped} skipped, ${failed} failed`);
      fetchAnalytics(); // Refresh data
    } catch (error) {
      console.error('Error sending re-engagement emails:', error);
      toast.error('Failed to send re-engagement emails');
    } finally {
      setSendingReengagement(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <Card className="card-base">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-forest" />
              <span className="font-medium text-forest">Date Range:</span>
            </div>
            <div className="flex gap-2">
              {['7d', '30d', '90d', 'custom'].map((preset) => (
                <Button
                  key={preset}
                  variant={analyticsPreset === preset ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAnalyticsPreset(preset)}
                  className={analyticsPreset === preset ? 'bg-forest text-white' : ''}
                >
                  {preset === '7d' ? 'Last 7 Days' : preset === '30d' ? 'Last 30 Days' : preset === '90d' ? 'Last 90 Days' : 'Custom'}
                </Button>
              ))}
            </div>
            {analyticsPreset === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={analyticsCustomStart}
                  onChange={(e) => setAnalyticsCustomStart(e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                />
                <span>to</span>
                <input
                  type="date"
                  value={analyticsCustomEnd}
                  onChange={(e) => setAnalyticsCustomEnd(e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                />
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAnalytics}
              disabled={analyticsLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${analyticsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {analyticsLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
        </div>
      ) : analyticsData ? (
        <>
          {/* Key Metrics Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-base hover:shadow-md transition-shadow">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-forest">{analyticsData.user_acquisition?.total_users || 0}</div>
                <div className="text-sm text-gray-500">Total Users</div>
                {analyticsData.user_acquisition?.new_signups > 0 && (
                  <div className="mt-2 flex items-center justify-center gap-1 text-xs text-green-600">
                    <TrendingUp className="h-3 w-3" />
                    <span>+{analyticsData.user_acquisition.new_signups} this period</span>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="card-base hover:shadow-md transition-shadow">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{analyticsData.user_acquisition?.new_signups || 0}</div>
                <div className="text-sm text-gray-500">New Signups</div>
                {analyticsData.user_acquisition?.change_percent !== undefined && (
                  <div className={`mt-2 flex items-center justify-center gap-1 text-xs ${
                    analyticsData.user_acquisition.change_percent >= 0 ? 'text-green-600' : 'text-red-500'
                  }`}>
                    {analyticsData.user_acquisition.change_percent >= 0 ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingUp className="h-3 w-3 rotate-180" />
                    )}
                    <span>
                      {analyticsData.user_acquisition.change_percent >= 0 ? '+' : ''}
                      {analyticsData.user_acquisition.change_percent.toFixed(1)}% vs prev
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="card-base hover:shadow-md transition-shadow">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-blue-600">{analyticsData.engagement?.active_users || 0}</div>
                <div className="text-sm text-gray-500">Active Users</div>
                {analyticsData.engagement?.active_rate !== undefined && (
                  <div className="mt-2 flex items-center justify-center gap-1 text-xs text-blue-600">
                    <Activity className="h-3 w-3" />
                    <span>{analyticsData.engagement.active_rate.toFixed(1)}% of total</span>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="card-base hover:shadow-md transition-shadow">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-amber-600">${analyticsData.revenue?.total_revenue?.toLocaleString() || 0}</div>
                <div className="text-sm text-gray-500">Revenue</div>
                {analyticsData.revenue?.change_percent !== undefined && (
                  <div className={`mt-2 flex items-center justify-center gap-1 text-xs ${
                    analyticsData.revenue.change_percent >= 0 ? 'text-green-600' : 'text-red-500'
                  }`}>
                    {analyticsData.revenue.change_percent >= 0 ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingUp className="h-3 w-3 rotate-180" />
                    )}
                    <span>
                      {analyticsData.revenue.change_percent >= 0 ? '+' : ''}
                      {analyticsData.revenue.change_percent.toFixed(1)}% vs prev
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Insights Section */}
          {insights.length > 0 && (
            <Card className="card-base border-l-4 border-l-amber-400">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-500" />
                  Quick Insights
                </CardTitle>
                <CardDescription>AI-powered observations from your data</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {insights.map((insight, index) => {
                    const IconComponent = insight.icon;
                    return (
                      <div 
                        key={index}
                        className={`flex items-start gap-3 p-3 rounded-lg ${insight.bgColor} transition-all hover:scale-[1.02]`}
                      >
                        <div className={`p-2 rounded-full bg-white shadow-sm`}>
                          <IconComponent className={`h-4 w-4 ${insight.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium text-sm ${insight.color}`}>{insight.title}</span>
                            {insight.priority === 'high' && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600">
                                Important
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{insight.message}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Export Buttons */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportAnalyticsCSV('users')}
              disabled={exportingCSV}
            >
              <Download className="h-4 w-4 mr-1" />
              Export Users
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportAnalyticsCSV('engagement')}
              disabled={exportingCSV}
            >
              <Download className="h-4 w-4 mr-1" />
              Export Engagement
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportAnalyticsCSV('revenue')}
              disabled={exportingCSV}
            >
              <Download className="h-4 w-4 mr-1" />
              Export Revenue
            </Button>
          </div>

          {/* Trend Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Signups Chart */}
            {analyticsData.user_acquisition?.daily_signups && analyticsData.user_acquisition.daily_signups.length > 0 && (
              <Card className="card-base">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                    <Users className="h-5 w-5 text-green-600" />
                    Daily Signups Trend
                  </CardTitle>
                  <CardDescription>New user registrations over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart 
                        data={analyticsData.user_acquisition.daily_signups.map(d => ({
                          date: parseLocalDate(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                          signups: d.count
                        }))}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="signupsGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          tickLine={false}
                          axisLine={{ stroke: '#e5e7eb' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          tickLine={false}
                          axisLine={false}
                          width={40}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                          }}
                          labelStyle={{ color: '#374151', fontWeight: 600 }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="signups" 
                          stroke="#22c55e" 
                          strokeWidth={2}
                          fill="url(#signupsGradient)" 
                          name="New Signups"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Daily Traffic Chart */}
            {pageViewsData?.daily_traffic && pageViewsData.daily_traffic.length > 0 && (
              <Card className="card-base">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                    <Activity className="h-5 w-5 text-blue-600" />
                    Daily Traffic Trend
                  </CardTitle>
                  <CardDescription>Page views and unique visitors</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart 
                        data={dailyTrafficChartData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          tickLine={false}
                          axisLine={{ stroke: '#e5e7eb' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          tickLine={false}
                          axisLine={false}
                          width={40}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                          }}
                          labelStyle={{ color: '#374151', fontWeight: 600 }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '10px' }}
                          iconType="circle"
                          iconSize={8}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="views" 
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          dot={{ r: 3, fill: '#3b82f6' }}
                          activeDot={{ r: 5 }}
                          name="Page Views"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="visitors" 
                          stroke="#8b5cf6" 
                          strokeWidth={2}
                          dot={{ r: 3, fill: '#8b5cf6' }}
                          activeDot={{ r: 5 }}
                          name="Unique Visitors"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Daily Active Users Chart */}
          {analyticsData.engagement?.daily_active_users && analyticsData.engagement.daily_active_users.length > 0 && (
            <Card className="card-base">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                  Daily Active Users
                </CardTitle>
                <CardDescription>Users who logged in and engaged with the platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart 
                      data={[...analyticsData.engagement.daily_active_users].reverse().map(d => ({
                        date: parseLocalDate(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                        dau: d.dau
                      }))}
                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="dauGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={false}
                        width={40}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                        labelStyle={{ color: '#374151', fontWeight: 600 }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="dau" 
                        stroke="#f59e0b" 
                        strokeWidth={2}
                        fill="url(#dauGradient)" 
                        name="Active Users"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cohort Retention Analysis */}
          {cohortData && cohortData.cohorts && cohortData.cohorts.length > 0 && (
            <Card className="card-base border-l-4 border-l-indigo-500">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                  <Users className="h-5 w-5 text-indigo-500" />
                  Cohort Retention Analysis
                </CardTitle>
                <CardDescription>
                  Track how many users from each signup month remain active over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Average Retention Summary */}
                {cohortData.average_retention && cohortData.average_retention.length > 1 && (
                  <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
                    <h4 className="text-sm font-medium text-indigo-700 mb-3 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Average Retention Rate
                    </h4>
                    <div className="flex gap-4 overflow-x-auto pb-2">
                      {cohortData.average_retention.map((ret, idx) => (
                        <div key={idx} className="text-center min-w-[60px]">
                          <div className={`text-lg font-bold ${
                            ret.average_rate >= 50 ? 'text-green-600' : 
                            ret.average_rate >= 25 ? 'text-amber-600' : 'text-red-500'
                          }`}>
                            {ret.average_rate}%
                          </div>
                          <div className="text-xs text-gray-500">{ret.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cohort Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Cohort</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-600">Users</th>
                        {[0, 1, 2, 3, 4, 5].map(m => (
                          <th key={m} className="text-center py-2 px-2 font-medium text-gray-600 min-w-[50px]">
                            M{m}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activeCohorts.map((cohort, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3 font-medium text-gray-700">{cohort.month}</td>
                          <td className="py-2 px-3 text-center">
                            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                              {cohort.total_signups}
                            </span>
                          </td>
                          {[0, 1, 2, 3, 4, 5].map(period => {
                            const retention = cohort.retention?.find(r => r.period === period);
                            if (!retention) {
                              return (
                                <td key={period} className="py-2 px-2 text-center">
                                  <span className="text-gray-300">—</span>
                                </td>
                              );
                            }
                            const rate = retention.retention_rate;
                            const bgIntensity = Math.min(Math.round(rate / 10), 10);
                            return (
                              <td key={period} className="py-2 px-2 text-center">
                                <div 
                                  className={`inline-flex items-center justify-center w-12 h-8 rounded text-xs font-medium transition-all hover:scale-110 ${
                                    rate >= 80 ? 'bg-green-500 text-white' :
                                    rate >= 60 ? 'bg-green-400 text-white' :
                                    rate >= 40 ? 'bg-green-300 text-green-900' :
                                    rate >= 20 ? 'bg-amber-200 text-amber-900' :
                                    rate > 0 ? 'bg-red-200 text-red-900' :
                                    'bg-gray-100 text-gray-500'
                                  }`}
                                  title={`${retention.active_users} of ${cohort.total_signups} users active`}
                                >
                                  {rate}%
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Legend */}
                <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <span className="font-medium">Retention Legend:</span>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-green-500"></div>
                    <span>80%+</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-green-300"></div>
                    <span>40-79%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-amber-200"></div>
                    <span>20-39%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-red-200"></div>
                    <span>&lt;20%</span>
                  </div>
                </div>

                {cohortData.cohorts.filter(c => c.total_signups > 0).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No cohort data available yet.</p>
                    <p className="text-sm mt-1">Data will appear as users sign up and become active.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Re-engagement Campaign Section */}
          {reengagementData && (
            <Card className="card-base border-l-4 border-l-rose-500">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                  <Mail className="h-5 w-5 text-rose-500" />
                  Re-engagement Campaigns
                </CardTitle>
                <CardDescription>
                  Bring back dormant users with automated email campaigns
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Dormant Users Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <div className="text-2xl font-bold text-amber-600">
                      {reengagementData.current_dormant?.day_7_count || 0}
                    </div>
                    <div className="text-sm text-gray-600">7+ Days Inactive</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">
                      {reengagementData.current_dormant?.day_14_count || 0}
                    </div>
                    <div className="text-sm text-gray-600">14+ Days Inactive</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {reengagementData.current_dormant?.day_30_count || 0}
                    </div>
                    <div className="text-sm text-gray-600">30+ Days Inactive</div>
                  </div>
                  <div className="text-center p-4 bg-gray-100 rounded-lg">
                    <div className="text-2xl font-bold text-gray-700">
                      {reengagementData.current_dormant?.total_dormant || 0}
                    </div>
                    <div className="text-sm text-gray-600">Total Dormant</div>
                  </div>
                </div>

                {/* Campaign Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Emails Sent (Last 30 Days)</span>
                    </div>
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      {reengagementData.emails_sent?.total || 0}
                    </div>
                    <div className="flex gap-3 text-xs text-gray-500">
                      <span>Day 7: {reengagementData.emails_sent?.by_type?.day_7 || 0}</span>
                      <span>Day 14: {reengagementData.emails_sent?.by_type?.day_14 || 0}</span>
                      <span>Day 30: {reengagementData.emails_sent?.by_type?.day_30 || 0}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-700">Campaign Effectiveness</span>
                    </div>
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {reengagementData.effectiveness?.return_rate || 0}%
                    </div>
                    <div className="text-xs text-gray-500">
                      {reengagementData.effectiveness?.users_returned || 0} users returned after re-engagement email
                    </div>
                  </div>
                </div>

                {/* Send Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => sendReengagementEmails('day_7')}
                    disabled={sendingReengagement || !reengagementData.current_dormant?.day_7_count}
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Send to 7-Day ({reengagementData.current_dormant?.day_7_count || 0})
                  </Button>
                  <Button
                    onClick={() => sendReengagementEmails('day_14')}
                    disabled={sendingReengagement || !reengagementData.current_dormant?.day_14_count}
                    variant="outline"
                    size="sm"
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Send to 14-Day ({reengagementData.current_dormant?.day_14_count || 0})
                  </Button>
                  <Button
                    onClick={() => sendReengagementEmails('day_30')}
                    disabled={sendingReengagement || !reengagementData.current_dormant?.day_30_count}
                    variant="outline"
                    size="sm"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Send to 30-Day ({reengagementData.current_dormant?.day_30_count || 0})
                  </Button>
                  <Button
                    onClick={() => sendReengagementEmails('all')}
                    disabled={sendingReengagement || !reengagementData.current_dormant?.total_dormant}
                    size="sm"
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                  >
                    {sendingReengagement ? (
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4 mr-1" />
                    )}
                    Send to All Dormant ({reengagementData.current_dormant?.total_dormant || 0})
                  </Button>
                </div>

                <p className="mt-4 text-xs text-gray-500">
                  <span className="font-medium text-green-600">Automated:</span> Re-engagement emails run automatically daily at 10 AM UTC.
                  Users who received an email in the last 7 days are skipped. Use manual buttons for immediate campaigns.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Page Views Analysis */}
          {pageViewsData && (
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Page Views Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{pageViewsData.summary?.total_page_views || pageViewsData.summary?.total_views || 0}</div>
                    <div className="text-sm text-gray-600">Total Page Views</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{pageViewsData.summary?.unique_visitors || pageViewsData.summary?.unique_users || 0}</div>
                    <div className="text-sm text-gray-600">Unique Visitors</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{(pageViewsData.summary?.avg_pages_per_visitor || pageViewsData.summary?.avg_views_per_user || 0).toFixed ? (pageViewsData.summary?.avg_pages_per_visitor || pageViewsData.summary?.avg_views_per_user || 0).toFixed(1) : (pageViewsData.summary?.avg_pages_per_visitor || pageViewsData.summary?.avg_views_per_user || 0)}</div>
                    <div className="text-sm text-gray-600">Avg Views/User</div>
                  </div>
                </div>
                
                {((pageViewsData.page_views && pageViewsData.page_views.length > 0) || (pageViewsData.by_page && Object.keys(pageViewsData.by_page).length > 0)) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Top Pages</h4>
                    <div className="space-y-2">
                      {pageViewsData.page_views 
                        ? pageViewsData.page_views.slice(0, 10).map((item) => (
                            <div key={item.page} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <span className="text-sm text-gray-600 truncate max-w-xs">{item.page}</span>
                              <span className="text-sm font-medium text-forest">{item.views} views</span>
                            </div>
                          ))
                        : Object.entries(pageViewsData.by_page)
                            .sort(([,a], [,b]) => b - a)
                            .slice(0, 10)
                            .map(([page, views]) => (
                              <div key={page} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-600 truncate max-w-xs">{page}</span>
                                <span className="text-sm font-medium text-forest">{views} views</span>
                              </div>
                            ))
                      }
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Engagement Metrics */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                <Clock className="h-5 w-5" />
                User Engagement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-xl font-bold text-forest">{analyticsData.engagement?.active_users || 0}</div>
                  <div className="text-xs text-gray-500">Active Users</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-xl font-bold text-forest">{analyticsData.engagement?.video_views || 0}</div>
                  <div className="text-xs text-gray-500">Video Views</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-xl font-bold text-forest">{analyticsData.engagement?.posts || 0}</div>
                  <div className="text-xs text-gray-500">Community Posts</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-xl font-bold text-forest">{analyticsData.engagement?.journal_entries || 0}</div>
                  <div className="text-xs text-gray-500">Journal Entries</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Login Activity */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                <LogIn className="h-5 w-5" />
                Login Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-600">{analyticsData.login_activity?.total_logins || 0}</div>
                  <div className="text-xs text-gray-500">Total Logins</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-xl font-bold text-blue-600">{analyticsData.login_activity?.unique_users || 0}</div>
                  <div className="text-xs text-gray-500">Unique Users</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-xl font-bold text-purple-600">
                    {analyticsData.login_activity?.avg_logins_per_user?.toFixed(1) || 0}
                  </div>
                  <div className="text-xs text-gray-500">Avg Logins/User</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Members */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                <Diamond className="h-5 w-5 text-amber-500" />
                Top Members (Diamond Points)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Rank</th>
                      <th className="text-left py-2 px-3">Member</th>
                      <th className="text-left py-2 px-3">Tiers</th>
                      <th className="text-right py-2 px-3">Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.top_members?.slice(0, 10).map((member, index) => (
                      <tr key={member.id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-2 px-3">
                          {index < 3 ? (
                            <span className={`text-lg ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : 'text-amber-600'}`}>
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                            </span>
                          ) : (
                            <span className="text-gray-400">#{index + 1}</span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <div className="font-medium">{member.full_name || member.email}</div>
                          <div className="text-xs text-gray-400">{member.email}</div>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex flex-wrap gap-1">
                            {member.active_tiers?.slice(0, 3).map(tier => (
                              <Badge key={tier} variant="outline" className="text-xs">
                                {tier.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right">
                          <span className="font-bold text-amber-600 flex items-center justify-end gap-1">
                            <Diamond className="h-4 w-4" />
                            {member.diamond_points}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Email Analytics Section */}
          {emailAnalytics && (
            <Card className="card-base border-l-4 border-l-rose-500">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                  <Mail className="h-5 w-5 text-rose-500" />
                  Follow-up Email Analytics
                </CardTitle>
                <CardDescription>
                  Track open rates and click-through rates for Collective applicant follow-up emails
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Summary Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-rose-50 rounded-lg">
                    <div className="text-2xl font-bold text-rose-600">{emailAnalytics.total_sent || 0}</div>
                    <div className="text-sm text-gray-600">Emails Sent</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{emailAnalytics.open_rate || 0}%</div>
                    <div className="text-sm text-gray-600">Open Rate</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{emailAnalytics.click_rate || 0}%</div>
                    <div className="text-sm text-gray-600">Click Rate</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{emailAnalytics.click_to_open_rate || 0}%</div>
                    <div className="text-sm text-gray-600">Click-to-Open</div>
                  </div>
                </div>

                {/* By Outcome Breakdown */}
                {Object.keys(emailAnalytics.by_outcome || {}).length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Performance by Outcome</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(emailAnalytics.by_outcome || {}).map(([outcome, stats]) => (
                        <div key={outcome} className="p-3 bg-gray-50 rounded-lg">
                          <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                            {outcome.replace(/_/g, ' ')}
                          </div>
                          <div className="text-lg font-bold text-forest">{stats.sent}</div>
                          <div className="text-xs text-gray-500">
                            {stats.sent > 0 ? Math.round(stats.opened / stats.sent * 100) : 0}% opened
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Day Breakdown */}
                {Object.keys(emailAnalytics.by_day || {}).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Performance by Follow-up Day</h4>
                    <div className="grid grid-cols-3 gap-3">
                      {['day_3', 'day_7', 'day_14'].map((dayKey) => {
                        const stats = emailAnalytics.by_day?.[dayKey] || { sent: 0, opened: 0, clicked: 0 };
                        const dayLabel = dayKey === 'day_3' ? 'Day 3' : dayKey === 'day_7' ? 'Day 7' : 'Day 14';
                        return (
                          <div key={dayKey} className="p-3 bg-gray-50 rounded-lg text-center">
                            <div className="text-xs font-medium text-gray-500 mb-1">{dayLabel}</div>
                            <div className="text-lg font-bold text-forest">{stats.sent}</div>
                            <div className="flex justify-center gap-2 text-xs text-gray-500">
                              <span className="text-green-600">{stats.opened} opened</span>
                              <span className="text-blue-600">{stats.clicked} clicked</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {emailAnalytics.total_sent === 0 && (
                  <div className="text-center py-6 text-gray-500">
                    <Mail className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No follow-up emails sent in this period.</p>
                    <p className="text-sm mt-1">Emails are sent automatically at Day 3, 7, and 14 after applications.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* A/B Testing Section */}
          {abTests.length > 0 && (
            <Card className="card-base border-l-4 border-l-purple-500">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-purple-500" />
                  A/B Email Testing
                </CardTitle>
                <CardDescription>
                  Compare different subject lines to optimize email performance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {abTests.map((test) => (
                    <div key={test.test_id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-forest">{test.test_name}</h4>
                          <p className="text-sm text-gray-500">Day {test.days_since_event} Follow-up</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={test.is_active ? "default" : "secondary"}>
                            {test.is_active ? "Active" : "Paused"}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleAbTest(test.test_id, test.is_active)}
                          >
                            {test.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => resetAbTest(test.test_id)}
                            title="Reset results"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Variants Comparison */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        {/* Variant A */}
                        <div className="p-3 bg-white rounded-lg border border-blue-200">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold">A</div>
                            <span className="text-xs text-gray-500">{test.variants?.A?.description}</span>
                          </div>
                          <p className="text-sm font-medium mb-2 text-gray-700">{test.variants?.A?.subject_template}</p>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <div className="text-lg font-bold text-gray-700">{test.results?.A?.sent || 0}</div>
                              <div className="text-xs text-gray-500">Sent</div>
                            </div>
                            <div>
                              <div className="text-lg font-bold text-green-600">{test.results?.A?.open_rate || 0}%</div>
                              <div className="text-xs text-gray-500">Opens</div>
                            </div>
                            <div>
                              <div className="text-lg font-bold text-blue-600">{test.results?.A?.click_rate || 0}%</div>
                              <div className="text-xs text-gray-500">Clicks</div>
                            </div>
                          </div>
                        </div>

                        {/* Variant B */}
                        <div className="p-3 bg-white rounded-lg border border-orange-200">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center font-bold">B</div>
                            <span className="text-xs text-gray-500">{test.variants?.B?.description}</span>
                          </div>
                          <p className="text-sm font-medium mb-2 text-gray-700">{test.variants?.B?.subject_template}</p>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <div className="text-lg font-bold text-gray-700">{test.results?.B?.sent || 0}</div>
                              <div className="text-xs text-gray-500">Sent</div>
                            </div>
                            <div>
                              <div className="text-lg font-bold text-green-600">{test.results?.B?.open_rate || 0}%</div>
                              <div className="text-xs text-gray-500">Opens</div>
                            </div>
                            <div>
                              <div className="text-lg font-bold text-blue-600">{test.results?.B?.click_rate || 0}%</div>
                              <div className="text-xs text-gray-500">Clicks</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Statistical Analysis */}
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="h-4 w-4 text-purple-500" />
                          <span className="text-sm font-medium text-purple-700">Statistical Analysis</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Open Rate: </span>
                            <span className={`font-medium ${test.analysis?.open_rate?.significant ? 'text-green-600' : 'text-gray-500'}`}>
                              {test.analysis?.open_rate?.message}
                            </span>
                            {test.analysis?.open_rate?.confidence > 0 && (
                              <span className="text-xs text-gray-400 ml-1">({test.analysis?.open_rate?.confidence}% conf.)</span>
                            )}
                          </div>
                          <div>
                            <span className="text-gray-600">Click Rate: </span>
                            <span className={`font-medium ${test.analysis?.click_rate?.significant ? 'text-green-600' : 'text-gray-500'}`}>
                              {test.analysis?.click_rate?.message}
                            </span>
                            {test.analysis?.click_rate?.confidence > 0 && (
                              <span className="text-xs text-gray-400 ml-1">({test.analysis?.click_rate?.confidence}% conf.)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {abTests.every(t => t.results?.A?.sent === 0 && t.results?.B?.sent === 0) && (
                  <div className="text-center py-4 text-gray-500 mt-4">
                    <FlaskConical className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No A/B test data yet. Results will appear as follow-up emails are sent.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card className="card-base">
          <CardContent className="p-12 text-center">
            <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No analytics data available. Click Refresh to load.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminAnalyticsTab;
